import { BellAlertIcon } from '@heroicons/react/24/solid'
import { ComponentProps, FC, useCallback, useMemo, useState } from 'react'
import InfiniteScroll from 'react-infinite-scroll-component'

import { useNotificationsListInfinite } from '@/api/models/notifications/hooks/useNotificationsListInfinite'
import { useSetNotificationReadAll } from '@/api/models/notifications/hooks/useSetNotificationReadAll'
import { useSetNotificationReadById } from '@/api/models/notifications/hooks/useSetNotificationReadById'
import { cn } from '@/app/utils'
import { ErrorCard, ErrorForbidden } from '@/components/pages/plugs'
import EmptyListPlug from '@/components/pages/plugs/EmptyListPlug'
import { AppButton } from '@/components/shared/button'
import { AppDrawer, useDrawerRectObserver } from '@/components/shared/drawer'
import { drawerScrollableContentWrapperId } from '@/components/shared/drawer/ui/Drawer'
import { ButtonSkeleton } from '@/components/shared/skeleton'
import { BadgesSkeleton } from '@/components/shared/skeleton/BadgesSkeleton'
import { isForbiddenError } from '@/utils/isForbiddenError'

import { NotificationList } from '.'
import { NotificationDrawerSkeleton } from './NotificationDrawerSkeleton'
import NotificationTypesFilter from './NotificationTypesFilter/NotificationTypesFilter'
import NotificationUnreadCount from './NotificationUnreadCount/NotificationUnreadCount'

type Props = Omit<
  ComponentProps<typeof AppDrawer>,
  'title' | 'beforeContentRender'
>

const NotificationDrawer: FC<Props> = props => {
  const [selectedTypes, setSelectedTypes] = useState<string[]>()

  const {
    data: { data: notificationList, unreadCount } = {},
    hasNextPage,
    isFetching: isNotificationListFetching,
    fetchNextPage,
    error: notificationListError,
    isError: isNotificationListError
  } = useNotificationsListInfinite({ typeAliases: selectedTypes })

  const notificationsLength = notificationList ? notificationList.length : 0
  const visibleNotifications = notificationList || []
  const isSkeletonShowing =
    isNotificationListFetching && notificationsLength === 0
  const isForbidden =
    !isSkeletonShowing &&
    notificationsLength === 0 &&
    isForbiddenError(notificationListError)
  const isErrorShowing =
    !isSkeletonShowing &&
    notificationsLength === 0 &&
    isNotificationListError &&
    !isForbidden

  const { mutate: setNotificationReaded } = useSetNotificationReadById()
  const { mutate: setNotificationReadedAll } = useSetNotificationReadAll({
    typeAlias: selectedTypes
  })

  const handleSetNotificationRead = useCallback(
    (id: string) => {
      setNotificationReaded({ id })
    },
    [setNotificationReaded]
  )

  const handleSetNotificationReadAll = useCallback(() => {
    setNotificationReadedAll()
  }, [setNotificationReadedAll])

  const { setHeaderRef, setBeforeContentRef, setFooterRef, contentHeight } =
    useDrawerRectObserver()

  const notificationListLoader = useMemo(
    () =>
      isNotificationListFetching && (
        <div className="mt-24 font-bold text-grey-80 text-16">
          Загружается...
        </div>
      ),
    [isNotificationListFetching]
  )

  const getBeforeContentComponent = () => {
    if (isSkeletonShowing && (selectedTypes?.length ?? 0) === 0) {
      return (
        <BadgesSkeleton
          rowsWithSizes={[57, 163, 81, 71, 126, 89, 126, 96]}
          className="flex-wrap gap-8 overflow-hidden pb-20 pt-8 md:flex-nowrap md:pb-12 md:pt-0 sm:pb-20"
          itemClassName="md:shrink-0 mr-0"
        />
      )
    }

    return (!isSkeletonShowing && notificationsLength <= 0 && !selectedTypes) ||
      isForbidden ||
      isErrorShowing ? null : (
      <NotificationTypesFilter
        className="mb-20 lg:!-mr-24 lg:!pr-24 xs:!-mr-16 xs:!pr-16"
        selectedTypes={selectedTypes}
        onFilterChange={setSelectedTypes}
        unreadCount={unreadCount ?? 0}
        isUnreadCountLoading={isNotificationListFetching}
      />
    )
  }

  const onClose = () => {
    props.onClose()
    setTimeout(() => setSelectedTypes(undefined), 300)
  }

  return (
    <AppDrawer
      {...props}
      ref={setHeaderRef}
      title={
        isSkeletonShowing && (selectedTypes?.length ?? 0) === 0 ? (
          <ButtonSkeleton className="h-[44px] w-[209px] md:h-[36px] md:w-[163px] sm:h-[28px] sm:w-[116px]" />
        ) : (
          'Уведомления'
        )
      }
      beforeContentRender={getBeforeContentComponent()}
      beforeContentRef={setBeforeContentRef}
      className={cn('w-[658px] lg:w-[362px] sm:w-full', props.className)}
      onClose={onClose}
    >
      {isSkeletonShowing && <NotificationDrawerSkeleton />}
      {!isSkeletonShowing && notificationsLength > 0 && (
        <>
          <InfiniteScroll
            dataLength={notificationsLength}
            next={fetchNextPage}
            hasMore={hasNextPage}
            loader={notificationListLoader}
            height={contentHeight}
            scrollableTarget={drawerScrollableContentWrapperId}
          >
            <NotificationList
              notifications={visibleNotifications}
              className="mb-40 lg:mb-24 sm:mb-20"
              onMarkAsRead={handleSetNotificationRead}
            />
          </InfiniteScroll>

          {Boolean(unreadCount) && (
            <div
              ref={setFooterRef}
              className="sticky bottom-0 bg-grey-10 pb-40 lg:pb-24 sm:pb-20"
            >
              <AppButton
                variant="secondary"
                size="large"
                className="flex w-full gap-8 sm:!h-48"
                onClick={handleSetNotificationReadAll}
              >
                Прочитать все
                <NotificationUnreadCount unread={{ count: unreadCount ?? 0 }} />
              </AppButton>
            </div>
          )}
        </>
      )}
      {isForbidden && <ErrorForbidden />}
      {isErrorShowing && (
        <div className="mt-[22%] sm:mt-[55%]">
          <ErrorCard />
        </div>
      )}
      {!isSkeletonShowing &&
        notificationsLength === 0 &&
        !isNotificationListError &&
        !selectedTypes?.length && (
          <EmptyListPlug
            icon={BellAlertIcon}
            title="Пока тут пусто"
            descriptionClassName="lg:w-[280px]"
            description="Как только вам придёт уведомление, оно появится здесь"
          />
        )}
      {!isSkeletonShowing &&
        notificationsLength === 0 &&
        !isNotificationListError &&
        selectedTypes?.length && <EmptyListPlug areFiltersApplied />}
    </AppDrawer>
  )
}

export default NotificationDrawer
